import { Card } from '@tempo-io/sui-card';
import { cn } from '@tempo-io/sui-class-utils';
import { forwardRef } from 'react';
import { bindCardSelection, useIsCardSelected } from '@/components/ui/swimlane/context';
import { useContextMenu } from '@/components/ui/swimlane/context-menu';
import type { ICardProps } from '@/components/ui/swimlane/types';
import { buildCardId } from '@/components/ui/swimlane/utils';
import { CARD_WIDTH, COMPACT_CARD_WIDTH, DEFAULT_COLOR } from '../../constants';
import {
  cardClickDisabledCssSelector,
  openCardDetailsCssSelector,
} from '../../hooks/useCardEventHandlers';

type StyledCardProps = React.ComponentProps<'div'> & {
  isSelected?: boolean;
  borderColor?: string;
  isMenuOpen?: boolean;
  isCompact?: boolean;
  children?: React.ReactNode;
};

export const StyledCard = forwardRef<HTMLDivElement, StyledCardProps>(
  (
    {
      isSelected,
      borderColor = DEFAULT_COLOR,
      isMenuOpen,
      isCompact,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <Card
        ref={ref}
        className={cn(
          'relative p-3 gap-2 rounded-sm border border-border-neutral/50 border-l-4 hover:shadow-md hover:z-[5]',
          `${openCardDetailsCssSelector}:cursor-pointer`,
          `[${openCardDetailsCssSelector}]:cursor-pointer`,
          `${cardClickDisabledCssSelector}:cursor-default`,
          `[${cardClickDisabledCssSelector}]:cursor-default`,
          className,
          {
            'shadow-lg': isSelected,
          },
        )}
        style={{
          ...style,
          borderLeftColor: borderColor,
          ...(isSelected && {
            outline: `1px solid ${borderColor}`,
          }),
          ...(isCompact ? { width: COMPACT_CARD_WIDTH.label } : { width: CARD_WIDTH.label }),
        }}
        {...props}
      >
        {children}
      </Card>
    );
  },
);

type SelectableCardProps = ICardProps &
  Omit<React.ComponentProps<'div'>, 'ref'> & {
    borderColor?: string;
    isCompact?: boolean;
  };

export const SelectableCard = forwardRef<HTMLDivElement, SelectableCardProps>(
  ({ itemID, rowID, columnID, borderColor = DEFAULT_COLOR, isCompact, ...props }, ref) => {
    const isSelected = useIsCardSelected({ itemID, rowID, columnID });
    const { contextMenu } = useContextMenu();
    const cardProps = { itemID, rowID, columnID };

    return (
      <StyledCard
        ref={ref}
        isSelected={isSelected}
        isCompact={isCompact}
        borderColor={borderColor}
        isMenuOpen={contextMenu.open && contextMenu.itemID === buildCardId(cardProps)}
        {...bindCardSelection(cardProps)}
        {...props}
      />
    );
  },
);
